import React, { useEffect, useContext, useState, useRef } from "react";
import { SimpleTooltip, SimpleUtility } from "simple-ui-components";
import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import RoomIcon from "@mui/icons-material/Room";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import MapIcon from "@mui/icons-material/Map";
import ViewListIcon from "@mui/icons-material/ViewList";

import {
  BodyWrapperStyled,
  BoxStyled,
  CardStyled,
  CardHeader,
  CardBody,
  OuterWrapper,
  PointsWithTechnicianStyled,
  EmptyList,
} from "../../theme";
import { get, del, put, post } from "../../server-requests/serverRequests";
import { AddRoutes } from "./addRoutes";
import { RoutesDetail } from "./routesDetail";
import { EditRoutes } from "./editRoute";
import { EditEquipments } from "./editEquipments";
import { EditTechnician } from "./editTechnician";
import { AuthContext } from "../authContextProvider";
import DeletePopup from "../shared/deletePopup";
import { LoadingList, LoadingBox } from "../shared/loading";
import { Layout, useWindowSize } from "../layout/otherPage";
import { AccountDetail } from "../accounts/accountDetail";
import { EquipmentDetailPopup } from "../dashboard/equipmentDetailPopup";
import { Maps } from "../shared/map";
import { PointDetailEquipment } from "./pointDetailEquipment";
import { getTranslation, LanguageContext } from "../languageProvider";
import { Log, ReplaceWord } from "../shared/utility";
import { EditDocuments } from "./editDocuments";

const filterActiveRoute = (list) => {
  let listToReturn = [];
  let notStartedYet = [];
  let alreadyStarted = [];
  for (let route of list) {
    if (!route.started || !route.ended) {
      listToReturn = [...listToReturn, route];
    }
  }
  for (let item of listToReturn) {
    if (!item.started) {
      notStartedYet = [...notStartedYet, item];
    } else {
      alreadyStarted = [...alreadyStarted, item];
    }
  }
  alreadyStarted = alreadyStarted.sort(
    (a, b) => new Date(b.started) - new Date(a.started)
  );
  return [...notStartedYet, ...alreadyStarted];
};

export const Routes = () => {
  const addRoutesRef = useRef();
  const editEquipmentRef = useRef();
  const editRoutesRef = useRef();
  const editTechnicianRef = useRef();
  const deleteRoutesRef = useRef();
  const equipmentDetailRef = useRef();
  const editDocumentsRef = useRef();

  const { Tooltip } = SimpleTooltip;
  const { GetIndexBy } = SimpleUtility;

  const { wrapperHeight, boxHeight } = useWindowSize();

  const { snackbarHandler } = useContext(AuthContext);
  const { lang } = useContext(LanguageContext);

  const [reloadRoutes, setReloadRoutes] = useState(true);
  const [routesList, setRoutesList] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);

  const [selectedRoute, setSelectedRoute] = useState("");

  const [mapReload, setMapReload] = useState(true);

  const [reloadEquipment, setReloadEquipment] = useState(true);
  const [equipmentList, setEquipmentList] = useState([]);

  const [view, setView] = useState("list"); // list and map
  const [marker, setMarker] = useState([]);

  const [reloadAccounts, setReloadAccounts] = useState(true);
  const [accountList, setAccountList] = useState([]);

  const [reloadTechnician, setReloadTechnician] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState([]);

  const [reloadPoints, setReloadPoints] = useState(false);
  const [pointsList, setPointsList] = useState([]);

  const listViewClickHandler = () => setView("list");
  const mapViewClickHandler = () => setView("map");

  // get Equipments List
  useEffect(() => {
    let isMounted = true;
    if (reloadEquipment) {
      get("equipments/list")
        .then((equipment) => {
          if (isMounted) {
            setEquipmentList(equipment);
            setReloadEquipment(false);
          }
        })
        .catch((status) => {
          snackbarHandler(getTranslation(`error${status}`));
          setReloadEquipment(false);
          Log("Error: get equipments/list" + status);
        });
    }
    return () => (isMounted = false);
  }, [reloadEquipment, snackbarHandler]);

  // get Routes List
  useEffect(() => {
    let isMounted = true;
    if (reloadRoutes) {
      get("routes/list")
        .then((routes) => {
          if (isMounted) {
            let list = filterActiveRoute(routes);
            setRoutesList(list);

            // selected Routes
            if (list.length > 0) {
              setSelectedRoute(list[0]);
              setReloadTechnician(true);
              setReloadPoints(true);
            } else {
              setSelectedRoute("");
              setSelectedTechnician([]);
              setPointsList([]);
            }
            setLoadingRoutes(false);
            setReloadRoutes(false);
          }
        })
        .catch((status) => {
          snackbarHandler(getTranslation(`error${status}`));
          setLoadingRoutes(false);
          setReloadRoutes(false);
          Log("Error: get routes/list" + status);
        });
    }
    return () => (isMounted = false);
  }, [reloadRoutes, snackbarHandler]);

  const selectRouteClickHandler = (route) => {
    setSelectedRoute(route);
    setReloadTechnician(true);
    setReloadPoints(true);
  };

  // routes
  const routeAddClickHandler = () => addRoutesRef.current.RoutesHandler();
  const editRouteClickHandler = (route) =>
    editRoutesRef.current.RoutesHandler(route);

  const updateRoutesSuccessHandler = () => setReloadRoutes(true);
  const updatePointSuccessHandler = () => setReloadPoints(true);
  const updateTechnicianSuccessHandler = () => setReloadTechnician(true);

  // delete
  const deleteRouteClickHandler = (id) => {
    deleteRoutesRef.current.Delete(
      id,
      `${getTranslation("0032")} ${
        getTranslation("0008") && getTranslation("0008").toLowerCase()
      }`,
      getTranslation("0120") &&
        ReplaceWord(
          getTranslation("0120"),
          "**NAME**",
          getTranslation("0008").toLowerCase()
        )
    );
  };

  const deleteRouteHandler = (id) => {
    del("routes", {
      id,
    })
      .then(() => {
        snackbarHandler(
          getTranslation("0121") &&
            ReplaceWord(
              getTranslation("0121"),
              "**NAME**",
              getTranslation("0008")
            ),
          "success"
        );
        setReloadRoutes(true);
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: delete routes" + status);
      });
  };

  // set Marker
  useEffect(() => {
    if (view === "map") {
      let equipmentsMarkersList = [];
      if (pointsList.length > 0) {
        equipmentsMarkersList = pointsList.map((point) => ({
          ...point.equipment,
          pos: {
            lat: Number(point.equipment.latitude),
            lng: Number(point.equipment.longitude),
          },
        }));
      }
      setMarker(equipmentsMarkersList);
    }
  }, [view, pointsList]);

  const editTechnicianClickHandler = (route) =>
    editTechnicianRef.current.TechnicianHandler(route);

  const editEquipmentClickHandler = (route) =>
    editEquipmentRef.current.EquipmentHandler(route);

  // get Account list
  useEffect(() => {
    let isMounted = true;
    if (reloadAccounts) {
      get("accounts/list")
        .then((account) => {
          if (isMounted) {
            let technician = account.filter(
              (item) =>
                item.role &&
                item.role.code === Number(process.env.REACT_APP_TECHNICIAN)
            );
            setAccountList(technician);
            setReloadAccounts(false);
          }
        })
        .catch((status) => {
          snackbarHandler(getTranslation(`error${status}`));
          Log("Error: get accounts/list" + status);
        });
    }
    return () => (isMounted = false);
  }, [reloadAccounts, snackbarHandler]);

  // get selected Points
  useEffect(() => {
    let isMounted = true;
    if (reloadPoints) {
      get("routes/points/list", {
        routeId: selectedRoute && selectedRoute.id,
      })
        .then((points) => {
          if (isMounted) {
            setPointsList(points);
            setReloadPoints(false);
          }
        })
        .catch((status) => {
          snackbarHandler(getTranslation(`error${status}`));
          setReloadPoints(false);
          Log("Error: get routes/technicians/list" + status);
        });
    }
    return () => (isMounted = false);
  }, [reloadPoints, selectedRoute, snackbarHandler]);

  // get selected technician
  useEffect(() => {
    let isMounted = true;
    if (reloadTechnician) {
      get("routes/technicians/list", {
        routeId: selectedRoute && selectedRoute.id,
      })
        .then((technician) => {
          if (isMounted) {
            setSelectedTechnician(technician);
            setReloadTechnician(false);
          }
        })
        .catch((status) => {
          snackbarHandler(getTranslation(`error${status}`));
          setReloadTechnician(false);
          Log("Error: get routes/technicians/list" + status);
        });
    }
    return () => (isMounted = false);
  }, [reloadTechnician, selectedRoute, snackbarHandler]);

  const disableClickHandler = () =>
    snackbarHandler(getTranslation("0138"), "info");

  const myLocationClickHandler = (pos) => {
    let newMarker = marker;
    let index = GetIndexBy(marker, 10, "id");
    if (index === -1) {
      setMarker([...newMarker, { id: 10, pos: pos }]);
    } else {
      newMarker[index] = { id: 10, pos: pos };
      setMarker(newMarker);
    }
    setMapReload((prev) => !prev);
  };

  const equipmentSelectHandler = (point) =>
    equipmentDetailRef.current.Detail(
      point.equipment,
      point.serviceOrders,
      point.id
    );

  const changeEquipmentIndexHandel = (
    action,
    localIndex,
    indexValue,
    pointId
  ) => {
    let toChanged = [];
    if (action === "up") {
      let previousPointIndexValue = pointsList[localIndex - 1].index;
      let previousPointId = pointsList[localIndex - 1].id;
      toChanged = [
        { id: pointId, index: previousPointIndexValue },
        { id: previousPointId, index: indexValue },
      ];
    } else {
      let nextPointIndexValue = pointsList[localIndex + 1].index;
      let nextPointId = pointsList[localIndex + 1].id;
      toChanged = [
        { id: pointId, index: nextPointIndexValue },
        { id: nextPointId, index: indexValue },
      ];
    }
    for (let point of toChanged) {
      put(
        "routes/points/index",
        { index: Number(point.index) },
        { id: point.id }
      )
        .then(() => setReloadPoints(true))
        .catch((status) => {
          snackbarHandler(getTranslation(`error${status}`));
        });
    }
  };

  // upload PDF
  const UploadPDFHandler = (toSend) => {
    if (toSend) {
      post("routes/points/service-orders", toSend)
        .then(() => {
          snackbarHandler(getTranslation("0168"), "success");
          setReloadPoints(true);
        })
        .catch((status) => snackbarHandler(getTranslation(`error${status}`)));
    }
  };

  const editDocumentClickHandler = (route) =>
    editDocumentsRef.current.Open(route);

  return (
    <Layout lang={lang}>
      <OuterWrapper height={wrapperHeight}>
        <BodyWrapperStyled fullHeight>
          <BoxStyled width="md" col="2" noMargin>
            <CardStyled>
              <CardHeader>
                <LocationSearchingIcon className="mainIcon" />
                <div className="detail" data-translate="0008">
                  {getTranslation("0008")}s
                  <span data-translate="0009">
                    {ReplaceWord(
                      getTranslation("0009"),
                      "**COUNT**",
                      routesList.length
                    )}
                  </span>
                </div>
                <Tooltip
                  text={`${getTranslation("0087")} ${getTranslation(
                    "0008"
                  ).toLowerCase()}`}
                  placement="left"
                >
                  <AddOutlinedIcon onClick={routeAddClickHandler} />
                </Tooltip>
              </CardHeader>
              <CardBody height={boxHeight}>
                {loadingRoutes && <LoadingList count={5} />}
                {!loadingRoutes &&
                  routesList.length > 0 &&
                  routesList.map((route) => (
                    <RoutesDetail
                      key={route.id}
                      route={route}
                      isActive={selectedRoute.id === route.id}
                      Edit={editRouteClickHandler}
                      EditTechnician={editTechnicianClickHandler}
                      EditEquipment={editEquipmentClickHandler}
                      EditDocument={editDocumentClickHandler}
                      Select={selectRouteClickHandler}
                      Delete={deleteRouteClickHandler}
                      Disable={disableClickHandler}
                    />
                  ))}
                {!loadingRoutes && routesList.length === 0 && (
                  <EmptyList data-translate="0008,0123">
                    {ReplaceWord(
                      getTranslation("0123"),
                      "**NAME**",
                      getTranslation("0008").toLowerCase()
                    )}
                  </EmptyList>
                )}
              </CardBody>
            </CardStyled>
          </BoxStyled>
          <BoxStyled width="md" col="2" noMargin>
            <CardStyled>
              <CardHeader>
                <RoomIcon className="mainIcon" />
                <div className="detail" data-translate="0081">
                  {selectedRoute ? selectedRoute.name : getTranslation("0081")}
                </div>
                {selectedRoute && pointsList.length > 0 && (
                  <>
                    <Tooltip
                      text={getTranslation("0011")}
                      className={view === "list" ? "active" : ""}
                      placement="left"
                      translate="0011"
                    >
                      <ViewListIcon onClick={listViewClickHandler} />
                    </Tooltip>
                    <Tooltip
                      text={getTranslation("0012")}
                      className={view === "map" ? "active" : ""}
                      placement="top"
                      translate="0012"
                    >
                      <MapIcon onClick={mapViewClickHandler} />
                    </Tooltip>
                  </>
                )}
              </CardHeader>
              <CardBody height={boxHeight} map={view === "map"}>
                <BodyWrapperStyled fullHeight>
                  {selectedRoute && (
                    <PointsWithTechnicianStyled height={boxHeight}>
                      <b data-translate="0088">{getTranslation("0088")}</b>
                      {selectedTechnician.length === 0 && (
                        <p data-translate="0008,0123">
                          {ReplaceWord(
                            getTranslation("0123"),
                            "**NAME**",
                            getTranslation("0008").toLowerCase()
                          )}
                        </p>
                      )}
                      {selectedTechnician.length > 0 &&
                        selectedTechnician.map((account, index) => (
                          <AccountDetail
                            key={index}
                            account={account}
                            isTechnician
                          />
                        ))}
                      <b data-translate="0079">{getTranslation("0079")}</b>
                      {loadingRoutes && view === "list" && (
                        <LoadingList count={5} />
                      )}
                      {loadingRoutes && view === "map" && (
                        <LoadingBox width="100%" height="100%" />
                      )}
                      {!loadingRoutes &&
                        view === "list" &&
                        pointsList.length > 0 &&
                        pointsList.map((point, index) => (
                          <PointDetailEquipment
                            key={point.id}
                            equipment={point.equipment}
                            Select={() => equipmentSelectHandler(point)}
                            length={pointsList.length}
                            index={index}
                            indexPoint={point.index}
                            pointId={point.id}
                            SwitchIndex={changeEquipmentIndexHandel}
                            serviceOrder={point.serviceOrders.length}
                          />
                        ))}
                      {!loadingRoutes && view === "map" && (
                        <Maps
                          list={marker}
                          mapHeight={"100%"}
                          MyLocationHandler={myLocationClickHandler}
                          mapReload={mapReload}
                        />
                      )}
                      {pointsList.length === 0 && (
                        <p data-translate="0035,0123">
                          {ReplaceWord(
                            getTranslation("0123"),
                            "**NAME**",
                            getTranslation("0035").toLowerCase()
                          )}
                        </p>
                      )}
                    </PointsWithTechnicianStyled>
                  )}
                  {!selectedRoute && (
                    <EmptyList data-translate="0079,0123">
                      {getTranslation("0123") &&
                        ReplaceWord(
                          getTranslation("0123"),
                          "**NAME**",
                          getTranslation("0079").toLowerCase()
                        )}
                    </EmptyList>
                  )}
                </BodyWrapperStyled>
              </CardBody>
            </CardStyled>
          </BoxStyled>
        </BodyWrapperStyled>
        <AddRoutes
          ref={addRoutesRef}
          equipmentList={equipmentList}
          technicianList={accountList}
          UpdateSuccess={updateRoutesSuccessHandler}
        />
        <EditEquipments
          ref={editEquipmentRef}
          equipmentList={equipmentList}
          UpdateSuccess={updatePointSuccessHandler}
        />
        <EditTechnician
          ref={editTechnicianRef}
          technicianList={accountList}
          UpdateSuccess={updateTechnicianSuccessHandler}
        />
        <EditRoutes
          ref={editRoutesRef}
          UpdateSuccess={updateRoutesSuccessHandler}
        />
        <EditDocuments
          ref={editDocumentsRef}
          UpdateSuccess={updateRoutesSuccessHandler}
        />
        <DeletePopup ref={deleteRoutesRef} DeleteSuccess={deleteRouteHandler} />
        <EquipmentDetailPopup
          ref={equipmentDetailRef}
          Uploaded={UploadPDFHandler}
          Reload={updatePointSuccessHandler}
        />
      </OuterWrapper>
    </Layout>
  );
};
